import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { partnerCertificationsService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const ExpiryStatus = ({ expiryDate }) => {
  const days = getDaysUntilExpiry(expiryDate);
  if (days === null) return <span className="px-8 py-3 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500">No Expiry</span>;
  if (days < 0) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-red-100 text-red-700"><Icon name="AlertTriangle" size={12} />Expired {Math.abs(days)}d ago</span>;
  if (days <= 7) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-red-100 text-red-700"><Icon name="AlertTriangle" size={12} />{days}d left — Critical</span>;
  if (days <= 30) return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700"><Icon name="Clock" size={12} />{days}d left</span>;
  return <span className="flex items-center gap-5 px-8 py-3 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700"><Icon name="CheckCircle2" size={12} />Valid — {days}d</span>;
};

const AddCertModal = ({ partners, onClose, onSuccess }) => {
  const [form, setForm] = useState({ partner_id: '', name: '', issuer: '', expiry_date: '', cert_status: 'active', cert_level: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.value }));
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name?.trim() || !form?.partner_id) { setError('Partner and certification name are required.'); return; }
    setSaving(true); setError(null);
    try { await partnerCertificationsService?.create(form); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to add certification.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-cyan-50">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center"><Icon name="Award" size={18} className="text-white" /></div>
            <h2 className="text-base font-bold text-slate-900">Add Certification</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Partner *</label>
            <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 bg-white" required>
              <option value="">— Select Partner —</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Certification Name *</label><input name="name" value={form?.name} onChange={handleChange} placeholder="e.g. AWS Solutions Architect Professional" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20" required /></div>
          <div className="grid grid-cols-2 gap-14">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Issuer</label><input name="issuer" value={form?.issuer} onChange={handleChange} placeholder="e.g. Amazon Web Services" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Level</label><input name="cert_level" value={form?.cert_level} onChange={handleChange} placeholder="e.g. Professional" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Expiry Date</label><input type="date" name="expiry_date" value={form?.expiry_date} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Status</label>
              <select name="cert_status" value={form?.cert_status} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 bg-white">
                {['active', 'expired', 'pending', 'revoked']?.map(s => <option key={s} value={s}>{s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
              </select></div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><Icon name="Plus" size={16} />Add Cert</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CertificationsTracker = () => {
  const toast = useToast();
  const [certs, setCerts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const [uploadTargetPartnerId, setUploadTargetPartnerId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cs, pts] = await Promise.all([partnerCertificationsService?.getAll(), partnerService?.getAll()]);
      setCerts(cs || []);
      setPartners(pts || []);
    } catch (err) { toast?.error('Failed to load certifications.'); }
    finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !uploadTargetId || !uploadTargetPartnerId) return;
    setUploadingId(uploadTargetId);
    try {
      await partnerCertificationsService?.uploadDocument(uploadTargetId, uploadTargetPartnerId, file);
      toast?.success('Document uploaded successfully.');
      loadData();
    } catch (err) { toast?.error('Upload failed: ' + err?.message); }
    finally { setUploadingId(null); setUploadTargetId(null); setUploadTargetPartnerId(null); if (fileInputRef?.current) fileInputRef.current.value = ''; }
  };

  const handleDownload = async (cert) => {
    if (!cert?.document_path) return;
    try {
      const url = await partnerCertificationsService?.getDocumentUrl(cert?.document_path);
      window.open(url, '_blank');
    } catch (err) { toast?.error('Failed to get document URL.'); }
  };

  const filtered = certs?.filter(c => {
    if (filterPartner !== 'all' && c?.partner_id !== filterPartner) return false;
    if (filterStatus !== 'all' && c?.cert_status !== filterStatus) return false;
    return true;
  });

  const expiringCount = certs?.filter(c => { const d = getDaysUntilExpiry(c?.expiry_date); return d !== null && d >= 0 && d <= 30; })?.length;
  const expiredCount = certs?.filter(c => { const d = getDaysUntilExpiry(c?.expiry_date); return d !== null && d < 0; })?.length;

  return (
    <div className="space-y-20">
      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={handleFileUpload} />

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16 mb-20">
          <div className="flex items-center gap-14">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/25">
              <Icon name="Award" size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Certifications Tracker</h2>
              <p className="text-sm text-slate-500 mt-1">Track partner certifications with document upload and expiry alerts</p>
            </div>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all">
            <Icon name="Plus" size={16} />Add Certification
          </button>
        </div>

        {(expiringCount > 0 || expiredCount > 0) && (
          <div className="flex flex-wrap gap-12 mb-20">
            {expiredCount > 0 && <div className="flex items-center gap-10 px-16 py-10 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium"><Icon name="AlertTriangle" size={16} />{expiredCount} certification{expiredCount > 1 ? 's' : ''} expired</div>}
            {expiringCount > 0 && <div className="flex items-center gap-10 px-16 py-10 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm font-medium"><Icon name="Clock" size={16} />{expiringCount} expiring within 30 days</div>}
          </div>
        )}

        <div className="flex flex-wrap gap-12">
          <select value={filterPartner} onChange={e => setFilterPartner(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 bg-white">
            <option value="all">All Partners</option>
            {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 bg-white">
            <option value="all">All Statuses</option>
            {['active', 'expired', 'pending', 'revoked']?.map(s => <option key={s} value={s}>{s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-16">
        {[
          { label: 'Total Certs', value: certs?.length, icon: 'Award', color: 'from-teal-500 to-cyan-600' },
          { label: 'Active', value: certs?.filter(c => c?.cert_status === 'active')?.length, icon: 'CheckCircle2', color: 'from-emerald-500 to-green-600' },
          { label: 'Expiring Soon', value: expiringCount, icon: 'Clock', color: 'from-amber-500 to-orange-600' },
          { label: 'Expired', value: expiredCount, icon: 'AlertTriangle', color: 'from-red-500 to-rose-600' },
        ]?.map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
            <div className="flex items-center justify-between mb-12">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat?.color} flex items-center justify-center`}><Icon name={stat?.icon} size={18} className="text-white" /></div>
              <span className="text-2xl font-bold text-slate-900">{stat?.value}</span>
            </div>
            <p className="text-xs text-slate-500 font-medium">{stat?.label}</p>
          </div>
        ))}
      </div>

      {/* Certs List */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="px-24 py-16 border-b border-slate-100"><span className="text-sm font-semibold text-slate-700">{filtered?.length} certification{filtered?.length !== 1 ? 's' : ''}</span></div>
        {loading ? (
          <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin" /></div>
        ) : filtered?.length === 0 ? (
          <div className="p-48 text-center"><Icon name="Award" size={40} className="text-slate-300 mx-auto mb-16" /><p className="text-slate-500 font-medium">No certifications found</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered?.map(cert => (
              <div key={cert?.id} className="px-24 py-18 hover:bg-slate-50/50 transition-colors">
                <div className="flex items-start justify-between gap-16">
                  <div className="flex items-start gap-14 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="Award" size={18} className="text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-8 mb-6">
                        <h3 className="text-sm font-semibold text-slate-900">{cert?.name}</h3>
                        <ExpiryStatus expiryDate={cert?.expiry_date} />
                      </div>
                      <div className="flex flex-wrap items-center gap-12 text-xs text-slate-500">
                        {cert?.partners && <span className="flex items-center gap-5"><Icon name="Building2" size={12} />{cert?.partners?.brand_name || cert?.partners?.legal_name}</span>}
                        {cert?.issuer && <span className="flex items-center gap-5"><Icon name="Shield" size={12} />{cert?.issuer}</span>}
                        {cert?.cert_level && <span className="flex items-center gap-5"><Icon name="Star" size={12} />{cert?.cert_level}</span>}
                        {cert?.expiry_date && <span className="flex items-center gap-5"><Icon name="Calendar" size={12} />Expires {new Date(cert?.expiry_date)?.toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 flex-shrink-0">
                    {cert?.document_path ? (
                      <button onClick={() => handleDownload(cert)} className="flex items-center gap-6 px-12 py-7 rounded-lg bg-teal-50 text-teal-700 text-xs font-semibold hover:bg-teal-100 transition-colors">
                        <Icon name="Download" size={13} />View Doc
                      </button>
                    ) : (
                      <button
                        onClick={() => { setUploadTargetId(cert?.id); setUploadTargetPartnerId(cert?.partner_id); fileInputRef?.current?.click(); }}
                        disabled={uploadingId === cert?.id}
                        className="flex items-center gap-6 px-12 py-7 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-colors disabled:opacity-60"
                      >
                        {uploadingId === cert?.id ? <div className="w-3 h-3 border-2 border-slate-400/30 border-t-slate-500 rounded-full animate-spin" /> : <Icon name="Upload" size={13} />}
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && <AddCertModal partners={partners} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); loadData(); toast?.success('Certification added.'); }} />}
    </div>
  );
};

export default CertificationsTracker;
